/**
 * In-product release notes / changelog. Source: packages/frontend/src/data/changelog.json,
 * maintained by Claude Code per the CLAUDE.md convention (every merged PR with user-visible
 * change appends an entry - feature → minor bump, improvement/fix → patch bump).
 */
import changelog from '@/data/changelog.json';

type EntryType = 'feature' | 'improvement' | 'fix';
type Entry = {
  version: string;
  date: string; // ISO date (YYYY-MM-DD)
  type: EntryType;
  title: string;
  description: string;
  pr?: number;
};

const TYPE_STYLES: Record<EntryType, { label: string; chip: string }> = {
  feature: {
    label: 'Feature',
    chip: 'bg-primary/10 text-primary border-primary/20',
  },
  improvement: {
    label: 'Improvement',
    chip: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  fix: {
    label: 'Fix',
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso + 'T12:00:00Z').toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ReleasesPage() {
  const entries = ((changelog as { entries?: Entry[] }).entries || []) as Entry[];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-12 lg:py-12">
      <header className="mb-8">
        <p className="mb-2 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-tertiary">
          What's new
        </p>
        <h1 className="mb-3 font-display text-3xl font-light leading-tight tracking-tight text-headline md:text-4xl">
          Release notes
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every shipped change to Upstand, newest first. {entries.length} release{entries.length === 1 ? '' : 's'}.
        </p>
      </header>

      <ol className="space-y-4">
        {entries.map((e) => {
          const styles = TYPE_STYLES[e.type] || TYPE_STYLES.improvement;
          return (
            <li
              key={`${e.version}-${e.pr ?? e.title}`}
              className="rounded-xl border border-outline-variant/30 bg-white/60 p-5 shadow-sm backdrop-blur-sm"
            >
              <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-mono text-sm font-semibold tabular-nums text-headline">
                  v{e.version}
                </span>
                <span
                  className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styles.chip}`}
                >
                  {styles.label}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">{formatDate(e.date)}</span>
              </div>
              <h2 className="mb-1 font-display text-base font-semibold text-headline">{e.title}</h2>
              <p className="text-sm leading-relaxed text-foreground/80">{e.description}</p>
            </li>
          );
        })}
      </ol>

      {entries.length === 0 ? (
        <p className="mt-8 text-sm italic text-muted-foreground">No releases yet.</p>
      ) : null}
    </div>
  );
}
